/**
 * PDF Export Utility for Fact Finder
 * Generates a downloadable PDF of the case packet
 */

/**
 * Export case packet to PDF
 * Uses a simple approach that works without external libraries
 * For production, consider using jsPDF or similar
 */
export function exportCasePacketToPdf({ analysis, caseData }) {
  // Create printable content
  const content = generatePrintableContent(analysis, caseData);
  
  // Open in new window for printing
  const printWindow = window.open('', '_blank');
  printWindow.document.write(content);
  printWindow.document.close();
  printWindow.focus();
  
  // Auto-trigger print dialog after content loads
  setTimeout(() => {
    printWindow.print();
  }, 500);
}

function generatePrintableContent(analysis, caseData) {
  const date = new Date().toLocaleDateString();
  const time = new Date().toLocaleTimeString();
  
  // Timeline HTML
  let timelineHtml = '';
  if (analysis?.timeline?.length > 0) {
    timelineHtml = `
      <div class="section">
        <h2>Timeline</h2>
        ${analysis.timeline.map(item => `
          <div class="timeline-item">
            <strong>${item.time}${item.approximate ? ' (approx.)' : ''}</strong>
            <p>${item.event}</p>
          </div>
        `).join('')}
      </div>
    `;
  }
  
  // Risk flags HTML
  let riskFlagsHtml = '';
  if (analysis?.risk_flags?.length > 0) {
    riskFlagsHtml = `
      <div class="section warning">
        <h2>Language Flags</h2>
        ${analysis.risk_flags.map(flag => `
          <div class="flag-item">
            <strong>${flag.label}</strong>
            <p class="flag-text">"${flag.text}"</p>
            ${flag.suggestion ? `<p class="suggestion">Suggested: ${flag.suggestion}</p>` : ''}
          </div>
        `).join('')}
      </div>
    `;
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>AskStewie Fact Finder Packet</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 800px;
          margin: 0 auto;
          padding: 40px 20px;
        }
        .header {
          text-align: center;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        .header h1 {
          font-size: 24px;
          margin-bottom: 5px;
        }
        .header .subtitle {
          color: #666;
          font-size: 14px;
        }
        .meta {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          background: #f5f5f5;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 30px;
        }
        .meta-item {
          flex: 1;
          min-width: 150px;
        }
        .meta-item label {
          font-weight: bold;
          font-size: 12px;
          text-transform: uppercase;
          color: #666;
          display: block;
        }
        .meta-item span {
          font-size: 14px;
        }
        .section {
          margin-bottom: 30px;
          page-break-inside: avoid;
        }
        .section h2 {
          font-size: 18px;
          border-bottom: 1px solid #ddd;
          padding-bottom: 10px;
          margin-bottom: 15px;
        }
        .section p, .section .content {
          white-space: pre-wrap;
          font-size: 14px;
        }
        .warning {
          background: #fff3cd;
          border: 1px solid #ffc107;
          padding: 15px;
          border-radius: 8px;
        }
        .warning h2 {
          color: #856404;
          border-color: #ffc107;
        }
        .timeline-item {
          border-left: 2px solid #333;
          padding-left: 15px;
          margin-bottom: 15px;
        }
        .flag-item {
          margin-bottom: 15px;
          padding-bottom: 15px;
          border-bottom: 1px solid #ffc107;
        }
        .flag-text {
          font-style: italic;
          color: #856404;
        }
        .suggestion {
          color: #155724;
          font-size: 13px;
        }
        .disclaimer {
          margin-top: 40px;
          padding: 15px;
          background: #e9ecef;
          border-radius: 8px;
          font-size: 12px;
          color: #666;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 12px;
          color: #999;
        }
        @media print {
          body {
            padding: 20px;
          }
          .section {
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>AskStewie Fact Finder Packet</h1>
        <div class="subtitle">Generated: ${date} at ${time}</div>
      </div>

      <div class="meta">
        <div class="meta-item">
          <label>Title</label>
          <span>${caseData?.title || 'Untitled Case'}</span>
        </div>
        <div class="meta-item">
          <label>Incident Type</label>
          <span>${caseData?.incident_type?.replace(/_/g, ' ') || 'N/A'}</span>
        </div>
        <div class="meta-item">
          <label>Date</label>
          <span>${caseData?.date || 'N/A'}</span>
        </div>
        <div class="meta-item">
          <label>Time</label>
          <span>${caseData?.time || 'N/A'}</span>
        </div>
        <div class="meta-item">
          <label>Location</label>
          <span>${caseData?.location || 'N/A'}</span>
        </div>
        <div class="meta-item">
          <label>Urgency</label>
          <span>${analysis?.urgency?.toUpperCase() || 'N/A'}</span>
        </div>
      </div>

      <div class="section">
        <h2>Fact Summary</h2>
        <div class="content">${analysis?.summary || 'No summary generated.'}</div>
      </div>

      <div class="section">
        <h2>Statement Draft</h2>
        <div class="content">${analysis?.statement_draft || 'No statement draft generated.'}</div>
      </div>

      <div class="section">
        <h2>Meeting Prep Sheet</h2>
        <div class="content">${analysis?.meeting_prep_sheet || 'No meeting prep sheet generated.'}</div>
      </div>

      ${timelineHtml}

      ${riskFlagsHtml}

      <div class="disclaimer">
        <strong>Disclaimer:</strong> This tool helps organize facts and prepare documentation based on the information you provide. Review all content carefully before using it. For formal guidance or representation, contact your union representative when appropriate.
      </div>

      <div class="footer">
        AskStewie Fact Finder &bull; For informational purposes only
      </div>
    </body>
    </html>
  `;
}
